import {
    useEffect,
    useMemo,
    useState,
    type CSSProperties,
    type RefObject,
} from "react";
import type { TextAreaProps } from "./TextArea.types";

const detectFieldSizingSupport = () =>
    typeof CSS !== "undefined" &&
    typeof CSS.supports === "function" &&
    CSS.supports("field-sizing", "content");

interface TextAreaAutoGrowStyle extends CSSProperties {
    "--textarea-min-rows"?: number;
    "--textarea-max-rows"?: number;
}

interface UseTextAreaAutoGrowProps {
    ref: RefObject<HTMLTextAreaElement | null>;
    isEnabled: boolean;
    minRows?: number;
    maxRows?: number;
    value?: TextAreaProps["value"];
}

/** Provides native field-sizing bounds and a scrollHeight fallback. */
export const useTextAreaAutoGrow = ({
    ref,
    isEnabled,
    minRows,
    maxRows,
    value,
}: UseTextAreaAutoGrowProps) => {
    const hasFieldSizingSupport = useMemo(() => detectFieldSizingSupport(), []);
    const [height, setHeight] = useState<string>();

    useEffect(() => {
        if (!isEnabled || hasFieldSizingSupport) {
            setHeight(undefined);
            return;
        }

        const textArea = ref.current;
        if (!textArea) {
            return;
        }

        const updateHeight = () => {
            const computedLineHeight = Number.parseFloat(
                getComputedStyle(textArea).lineHeight,
            );
            const lineHeight =
                Number.isFinite(computedLineHeight) && computedLineHeight > 0
                    ? computedLineHeight
                    : 20;
            const minHeight = (minRows ?? 1) * lineHeight;
            const maxHeight =
                maxRows === undefined ? Infinity : maxRows * lineHeight;
            // scrollHeight never reports less than the current box, so the
            // inline height is released before measuring or the field could
            // only ever grow.
            textArea.style.height = "";
            const nextHeight = Math.min(
                Math.max(textArea.scrollHeight, minHeight),
                maxHeight,
            );
            textArea.style.height = `${nextHeight}px`;
            setHeight(`${nextHeight}px`);
        };

        updateHeight();
        textArea.addEventListener("input", updateHeight);
        return () => {
            textArea.removeEventListener("input", updateHeight);
        };
    }, [hasFieldSizingSupport, isEnabled, maxRows, minRows, ref, value]);

    const style: TextAreaAutoGrowStyle = {
        "--textarea-min-rows": minRows,
        "--textarea-max-rows": maxRows,
        height: isEnabled && !hasFieldSizingSupport ? height : undefined,
    };

    return { style };
};
